/**
 * EINE Ländertabelle (WS-02). Freitext → ISO-3166-1 alpha-2, Land → Steuerzone (WS-01).
 * Unbekanntes wird nie geraten: leer/unbekannt ergibt null bzw. Zone 'UNKNOWN' (ST-12).
 */
import type { CountryCode, Language, TaxZone } from "./types.js";

export interface CountryEntry {
  code: CountryCode;
  eu: boolean;
  name: { de: string; en: string };
  /** weitere Schreibweisen (Landessprache, Kurzformen, Teilgebiete) */
  aliases: string[];
}

const C = (code: string, eu: boolean, de: string, en: string, aliases: string[] = []): CountryEntry => ({
  code,
  eu,
  name: { de, en },
  aliases,
});

export const COUNTRIES: readonly CountryEntry[] = [
  // DACH
  C("DE", true, "Deutschland", "Germany", ["Bundesrepublik Deutschland", "BRD", "Federal Republic of Germany", "DEU", "GER"]),
  C("AT", true, "Österreich", "Austria", ["Oesterreich", "Republik Österreich", "AUT"]),
  C("CH", false, "Schweiz", "Switzerland", ["Suisse", "Svizzera", "Svizra", "Schweizerische Eidgenossenschaft", "Confoederatio Helvetica", "CHE"]),
  // übrige EU-27
  C("BE", true, "Belgien", "Belgium", ["Belgique", "België", "Belgie"]),
  C("BG", true, "Bulgarien", "Bulgaria", ["България"]),
  C("CY", true, "Zypern", "Cyprus", ["Κύπρος"]),
  C("CZ", true, "Tschechien", "Czech Republic", ["Czechia", "Česko", "Česká republika", "Tschechische Republik"]),
  C("DK", true, "Dänemark", "Denmark", ["Danmark"]),
  C("EE", true, "Estland", "Estonia", ["Eesti"]),
  C("ES", true, "Spanien", "Spain", ["España", "Espana"]),
  C("FI", true, "Finnland", "Finland", ["Suomi"]),
  C("FR", true, "Frankreich", "France", ["République française"]),
  C("GR", true, "Griechenland", "Greece", ["Hellas", "Ελλάδα", "EL"]),
  C("HR", true, "Kroatien", "Croatia", ["Hrvatska"]),
  C("HU", true, "Ungarn", "Hungary", ["Magyarország"]),
  C("IE", true, "Irland", "Ireland", ["Éire", "Republic of Ireland"]),
  C("IT", true, "Italien", "Italy", ["Italia"]),
  C("LT", true, "Litauen", "Lithuania", ["Lietuva"]),
  C("LU", true, "Luxemburg", "Luxembourg", ["Lëtzebuerg"]),
  C("LV", true, "Lettland", "Latvia", ["Latvija"]),
  C("MT", true, "Malta", "Malta"),
  C("NL", true, "Niederlande", "Netherlands", ["Nederland", "Holland", "The Netherlands"]),
  C("PL", true, "Polen", "Poland", ["Polska"]),
  C("PT", true, "Portugal", "Portugal"),
  C("RO", true, "Rumänien", "Romania", ["România"]),
  C("SE", true, "Schweden", "Sweden", ["Sverige"]),
  C("SI", true, "Slowenien", "Slovenia", ["Slovenija"]),
  C("SK", true, "Slowakei", "Slovakia", ["Slovensko", "Slowakische Republik"]),
  // Europa außerhalb der EU
  C("LI", false, "Liechtenstein", "Liechtenstein", ["Fürstentum Liechtenstein"]),
  C("NO", false, "Norwegen", "Norway", ["Norge"]),
  C("IS", false, "Island", "Iceland", ["Ísland"]),
  C("GB", false, "Vereinigtes Königreich", "United Kingdom", [
    "UK", "Great Britain", "Großbritannien", "Britain", "England", "Scotland", "Schottland", "Wales",
    "Northern Ireland", "Nordirland", "U.K.",
  ]),
  C("MC", false, "Monaco", "Monaco"),
  C("SM", false, "San Marino", "San Marino"),
  C("AD", false, "Andorra", "Andorra"),
  C("RS", false, "Serbien", "Serbia", ["Srbija"]),
  C("BA", false, "Bosnien und Herzegowina", "Bosnia and Herzegovina", ["Bosnien-Herzegowina"]),
  C("ME", false, "Montenegro", "Montenegro"),
  C("MK", false, "Nordmazedonien", "North Macedonia", ["Mazedonien"]),
  C("AL", false, "Albanien", "Albania", ["Shqipëria"]),
  C("TR", false, "Türkei", "Turkey", ["Türkiye", "Turkiye", "Tuerkei"]),
  C("UA", false, "Ukraine", "Ukraine", ["Україна"]),
  C("RU", false, "Russland", "Russia", ["Russian Federation", "Russische Föderation"]),
  // Amerika
  C("US", false, "Vereinigte Staaten", "United States", [
    "USA", "U.S.A.", "U.S.", "United States of America", "Vereinigte Staaten von Amerika", "Amerika", "America",
  ]),
  C("CA", false, "Kanada", "Canada"),
  C("MX", false, "Mexiko", "Mexico", ["México"]),
  C("BR", false, "Brasilien", "Brazil", ["Brasil"]),
  C("AR", false, "Argentinien", "Argentina"),
  // Nahost/Afrika
  C("AE", false, "Vereinigte Arabische Emirate", "United Arab Emirates", ["VAE", "UAE", "U.A.E.", "Emirate", "Emirates"]),
  C("SA", false, "Saudi-Arabien", "Saudi Arabia", ["KSA"]),
  C("QA", false, "Katar", "Qatar"),
  C("KW", false, "Kuwait", "Kuwait"),
  C("BH", false, "Bahrain", "Bahrain"),
  C("OM", false, "Oman", "Oman"),
  C("IL", false, "Israel", "Israel"),
  C("EG", false, "Ägypten", "Egypt"),
  C("MA", false, "Marokko", "Morocco", ["Maroc"]),
  C("ZA", false, "Südafrika", "South Africa"),
  // Asien/Pazifik
  C("CN", false, "China", "China", ["Volksrepublik China", "People's Republic of China", "PRC"]),
  C("HK", false, "Hongkong", "Hong Kong", ["Hong Kong SAR"]),
  C("TW", false, "Taiwan", "Taiwan"),
  C("JP", false, "Japan", "Japan"),
  C("KR", false, "Südkorea", "South Korea", ["Korea", "Republic of Korea"]),
  C("SG", false, "Singapur", "Singapore"),
  C("IN", false, "Indien", "India", ["Bharat"]),
  C("TH", false, "Thailand", "Thailand"),
  C("ID", false, "Indonesien", "Indonesia"),
  C("MY", false, "Malaysia", "Malaysia"),
  C("PH", false, "Philippinen", "Philippines"),
  C("VN", false, "Vietnam", "Vietnam", ["Viet Nam"]),
  C("AU", false, "Australien", "Australia"),
  C("NZ", false, "Neuseeland", "New Zealand"),
  // Übrige Staaten (Creator-Prüfung, Befund 29): vollständige Liste; alle ohne EU-Mitgliedschaft, Steuerzone NON_EU (Drittland)
  C("BY", false, "Belarus", "Belarus", ["Weißrussland", "Weissrussland"]),
  C("MD", false, "Moldau", "Moldova", ["Republik Moldau"]),
  C("VA", false, "Vatikanstadt", "Vatican City", ["Vatikan", "Holy See"]),
  C("XK", false, "Kosovo", "Kosovo"),
  C("GE", false, "Georgien", "Georgia", ["Sakartwelo"]),
  C("AM", false, "Armenien", "Armenia"),
  C("AZ", false, "Aserbaidschan", "Azerbaijan"),
  C("KZ", false, "Kasachstan", "Kazakhstan"),
  C("KG", false, "Kirgisistan", "Kyrgyzstan"),
  C("TJ", false, "Tadschikistan", "Tajikistan"),
  C("TM", false, "Turkmenistan", "Turkmenistan"),
  C("UZ", false, "Usbekistan", "Uzbekistan"),
  C("AF", false, "Afghanistan", "Afghanistan"),
  C("PK", false, "Pakistan", "Pakistan"),
  C("BD", false, "Bangladesch", "Bangladesh"),
  C("LK", false, "Sri Lanka", "Sri Lanka"),
  C("NP", false, "Nepal", "Nepal"),
  C("BT", false, "Bhutan", "Bhutan"),
  C("MV", false, "Malediven", "Maldives"),
  C("MN", false, "Mongolei", "Mongolia"),
  C("KP", false, "Nordkorea", "North Korea"),
  C("MO", false, "Macau", "Macao", ["Macao SAR"]),
  C("MM", false, "Myanmar", "Myanmar", ["Burma", "Birma"]),
  C("LA", false, "Laos", "Laos"),
  C("KH", false, "Kambodscha", "Cambodia"),
  C("BN", false, "Brunei", "Brunei", ["Brunei Darussalam"]),
  C("TL", false, "Timor-Leste", "Timor-Leste", ["Osttimor", "East Timor"]),
  C("IQ", false, "Irak", "Iraq"),
  C("IR", false, "Iran", "Iran"),
  C("JO", false, "Jordanien", "Jordan"),
  C("LB", false, "Libanon", "Lebanon"),
  C("SY", false, "Syrien", "Syria"),
  C("PS", false, "Palästina", "Palestine", ["Palaestina"]),
  C("YE", false, "Jemen", "Yemen"),
  C("DZ", false, "Algerien", "Algeria"),
  C("TN", false, "Tunesien", "Tunisia"),
  C("LY", false, "Libyen", "Libya"),
  C("SD", false, "Sudan", "Sudan"),
  C("SS", false, "Südsudan", "South Sudan"),
  C("ET", false, "Äthiopien", "Ethiopia"),
  C("ER", false, "Eritrea", "Eritrea"),
  C("DJ", false, "Dschibuti", "Djibouti"),
  C("SO", false, "Somalia", "Somalia"),
  C("KE", false, "Kenia", "Kenya"),
  C("UG", false, "Uganda", "Uganda"),
  C("RW", false, "Ruanda", "Rwanda"),
  C("BI", false, "Burundi", "Burundi"),
  C("TZ", false, "Tansania", "Tanzania"),
  C("MZ", false, "Mosambik", "Mozambique"),
  C("MW", false, "Malawi", "Malawi"),
  C("ZM", false, "Sambia", "Zambia"),
  C("ZW", false, "Simbabwe", "Zimbabwe"),
  C("BW", false, "Botsuana", "Botswana"),
  C("NA", false, "Namibia", "Namibia"),
  C("LS", false, "Lesotho", "Lesotho"),
  C("SZ", false, "Eswatini", "Eswatini", ["Swasiland", "Swaziland"]),
  C("MG", false, "Madagaskar", "Madagascar"),
  C("MU", false, "Mauritius", "Mauritius"),
  C("SC", false, "Seychellen", "Seychelles"),
  C("KM", false, "Komoren", "Comoros"),
  C("AO", false, "Angola", "Angola"),
  C("CD", false, "Demokratische Republik Kongo", "Democratic Republic of the Congo", ["DR Kongo", "DR Congo"]),
  C("CG", false, "Republik Kongo", "Republic of the Congo", ["Kongo", "Congo"]),
  C("GA", false, "Gabun", "Gabon"),
  C("GQ", false, "Äquatorialguinea", "Equatorial Guinea"),
  C("CM", false, "Kamerun", "Cameroon"),
  C("CF", false, "Zentralafrikanische Republik", "Central African Republic"),
  C("TD", false, "Tschad", "Chad"),
  C("NE", false, "Niger", "Niger"),
  C("NG", false, "Nigeria", "Nigeria"),
  C("BJ", false, "Benin", "Benin"),
  C("TG", false, "Togo", "Togo"),
  C("GH", false, "Ghana", "Ghana"),
  C("CI", false, "Elfenbeinküste", "Côte d’Ivoire", ["Ivory Coast", "Cote d'Ivoire", "Elfenbeinkueste"]),
  C("BF", false, "Burkina Faso", "Burkina Faso"),
  C("ML", false, "Mali", "Mali"),
  C("MR", false, "Mauretanien", "Mauritania"),
  C("SN", false, "Senegal", "Senegal"),
  C("GM", false, "Gambia", "Gambia"),
  C("GW", false, "Guinea-Bissau", "Guinea-Bissau"),
  C("GN", false, "Guinea", "Guinea"),
  C("SL", false, "Sierra Leone", "Sierra Leone"),
  C("LR", false, "Liberia", "Liberia"),
  C("CV", false, "Kap Verde", "Cabo Verde", ["Cape Verde"]),
  C("ST", false, "São Tomé und Príncipe", "São Tomé and Príncipe", ["Sao Tome and Principe"]),
  C("CL", false, "Chile", "Chile"),
  C("CO", false, "Kolumbien", "Colombia"),
  C("PE", false, "Peru", "Peru"),
  C("EC", false, "Ecuador", "Ecuador"),
  C("BO", false, "Bolivien", "Bolivia"),
  C("PY", false, "Paraguay", "Paraguay"),
  C("UY", false, "Uruguay", "Uruguay"),
  C("VE", false, "Venezuela", "Venezuela"),
  C("GY", false, "Guyana", "Guyana"),
  C("SR", false, "Suriname", "Suriname"),
  C("PA", false, "Panama", "Panama", ["Panamá"]),
  C("CR", false, "Costa Rica", "Costa Rica"),
  C("NI", false, "Nicaragua", "Nicaragua"),
  C("HN", false, "Honduras", "Honduras"),
  C("SV", false, "El Salvador", "El Salvador"),
  C("GT", false, "Guatemala", "Guatemala"),
  C("BZ", false, "Belize", "Belize"),
  C("CU", false, "Kuba", "Cuba"),
  C("JM", false, "Jamaika", "Jamaica"),
  C("HT", false, "Haiti", "Haiti"),
  C("DO", false, "Dominikanische Republik", "Dominican Republic"),
  C("PR", false, "Puerto Rico", "Puerto Rico"),
  C("BS", false, "Bahamas", "Bahamas"),
  C("BB", false, "Barbados", "Barbados"),
  C("TT", false, "Trinidad und Tobago", "Trinidad and Tobago"),
  C("AG", false, "Antigua und Barbuda", "Antigua and Barbuda"),
  C("DM", false, "Dominica", "Dominica"),
  C("GD", false, "Grenada", "Grenada"),
  C("KN", false, "St. Kitts und Nevis", "Saint Kitts and Nevis"),
  C("LC", false, "St. Lucia", "Saint Lucia"),
  C("VC", false, "St. Vincent und die Grenadinen", "Saint Vincent and the Grenadines"),
  C("PG", false, "Papua-Neuguinea", "Papua New Guinea"),
  C("FJ", false, "Fidschi", "Fiji"),
  C("SB", false, "Salomonen", "Solomon Islands"),
  C("VU", false, "Vanuatu", "Vanuatu"),
  C("WS", false, "Samoa", "Samoa"),
  C("TO", false, "Tonga", "Tonga"),
  C("KI", false, "Kiribati", "Kiribati"),
  C("TV", false, "Tuvalu", "Tuvalu"),
  C("NR", false, "Nauru", "Nauru"),
  C("PW", false, "Palau", "Palau"),
  C("MH", false, "Marshallinseln", "Marshall Islands"),
  C("FM", false, "Mikronesien", "Micronesia"),
];

/** Namen, die als Wort in Adresszeilen zu mehrdeutig sind (nur als ganzer Wert erlaubt). */
const AMBIGUOUS_IN_TEXT = new Set(["emirates", "emirate", "america", "amerika", "korea", "holland", "britain", "island",
  // US-Bundesstaat, Vorname, Wörter in Adresszeilen (Befund 29: vollständige Liste ohne neue Fehltreffer)
  "georgia", "jordan", "chad", "niger", "guinea", "mali", "dominica", "congo", "kongo", "panama", "chile", "samoa", "togo", "sudan", "benin"]);

/**
 * Städte als Hinweis für die Adress-Erkennung (ST-13/ST-14). Nur als ganzes Wort;
 * „Londoner Straße“ ist also nicht London.
 */
export const CITY_HINTS: Readonly<Record<string, CountryCode>> = {
  berlin: "DE", hamburg: "DE", munchen: "DE", munich: "DE", koln: "DE", cologne: "DE", frankfurt: "DE",
  stuttgart: "DE", dusseldorf: "DE", dortmund: "DE", leipzig: "DE", bremen: "DE", dresden: "DE",
  hannover: "DE", nurnberg: "DE", nuremberg: "DE", duisburg: "DE", bochum: "DE", wuppertal: "DE",
  bielefeld: "DE", bonn: "DE", munster: "DE", mannheim: "DE", karlsruhe: "DE", augsburg: "DE", wiesbaden: "DE",
  wien: "AT", vienna: "AT", graz: "AT", linz: "AT", salzburg: "AT", innsbruck: "AT", klagenfurt: "AT",
  zurich: "CH", genf: "CH", geneve: "CH", geneva: "CH", basel: "CH", bern: "CH", lausanne: "CH",
  luzern: "CH", lucerne: "CH", "st. gallen": "CH", "st gallen": "CH", lugano: "CH", winterthur: "CH",
  london: "GB", manchester: "GB", birmingham: "GB", edinburgh: "GB", glasgow: "GB", belfast: "GB",
  paris: "FR", amsterdam: "NL", praha: "CZ", prag: "CZ", prague: "CZ", dubai: "AE", "abu dhabi": "AE",
  "new york": "US",
};

/** Kleinschreibung, Diakritika weg, ß → ss, Punkte weg, Leerraum zusammenfassen. */
export function foldText(input: string): string {
  return input
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/ß/g, "ss")
    .replace(/(?<=\p{L})\.(?=\p{L}|$)/gu, "")
    .replace(/\s+/g, " ")
    .trim();
}

const BY_CODE = new Map<string, CountryEntry>(COUNTRIES.map((c) => [c.code, c]));
const BY_NAME = new Map<string, CountryCode>();
for (const c of COUNTRIES) {
  for (const n of [c.name.de, c.name.en, ...c.aliases]) BY_NAME.set(foldText(n), c.code);
}
// Umschreibungen ohne Umlaut, die foldText nicht erzeugt
BY_NAME.set("oesterreich", "AT");
BY_NAME.set("tuerkei", "TR");
BY_NAME.set("daenemark", "DK");
BY_NAME.set("suedafrika", "ZA");
BY_NAME.set("suedkorea", "KR");

export function getCountry(code: CountryCode): CountryEntry | undefined {
  return BY_CODE.get(code.toUpperCase());
}

/**
 * Freitext → ISO-Code (WS-02). ISO-Codes (alpha-2) zählen nur als ganzer Wert.
 * Zonenwörter wie „EU“ oder „non_eu“ sind KEIN Land und ergeben null.
 */
export function normalizeCountry(input: string | null | undefined): CountryCode | null {
  if (input == null) return null;
  const raw = input.trim();
  if (raw === "") return null;
  if (/^[A-Za-z]{2}$/.test(raw)) {
    const up = raw.toUpperCase();
    if (BY_CODE.has(up)) return up;
    if (up === "EL") return "GR";
    if (up === "UK") return "GB";
    return null;
  }
  return BY_NAME.get(foldText(raw)) ?? null;
}

/** Steuerzone eines Landes (WS-01): DE/AT/CH je eigen, EU, NON_EU; leer/unbekannt → UNKNOWN. */
export function zoneOfCountry(input: string | null | undefined): TaxZone {
  const code = normalizeCountry(input);
  if (!code) return "UNKNOWN";
  if (code === "DE" || code === "AT" || code === "CH") return code;
  return BY_CODE.get(code)?.eu ? "EU" : "NON_EU";
}

export function isEuCountry(code: CountryCode | null): boolean {
  return !!code && !!BY_CODE.get(code)?.eu;
}

export function countryName(code: CountryCode, lang: Language): string {
  return BY_CODE.get(code)?.name[lang] ?? code;
}

function containsWord(haystack: string, needle: string): boolean {
  const esc = needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(?<![\\p{L}\\p{N}])${esc}(?![\\p{L}\\p{N}])`, "u").test(haystack);
}

const TEXT_NAMES = [...BY_NAME.entries()]
  .filter(([n]) => n.length >= 4 && !AMBIGUOUS_IN_TEXT.has(n))
  .sort((a, b) => b[0].length - a[0].length);
const CITY_ENTRIES = Object.entries(CITY_HINTS).sort((a, b) => b[0].length - a[0].length);

/** Regionen, deren Name einen Ländernamen enthält, ohne das Land zu sein (ST-12: nie raten; R-02). */
const NON_COUNTRY_REGIONS = ["new mexico", "new jersey", "new england", "west virginia", "nova scotia", "new caledonia"];

/**
 * Land aus einer Adresse (ST-14): Zeilen/Segmente von hinten nach vorn. Je Segment zuerst
 * der ganze Wert (auch ISO-Code), dann Ländernamen als ganzes Wort (nicht als Teil eines
 * Regionsnamens wie „New Mexico“). Städte zählen nur in der letzten Zeile: „Paris, Texas“
 * endet mit einer unbekannten Region und bleibt deshalb unbestimmt (R-02).
 */
export function countryFromAddress(address: string | readonly string[] | null | undefined): CountryCode | null {
  if (!address) return null;
  const lines = (typeof address === "string" ? address.split(/\r?\n/) : [...address])
    .flatMap((l) => l.split(","))
    .map((s) => s.trim())
    .filter(Boolean);
  for (let i = lines.length - 1; i >= 0; i--) {
    const seg = lines[i]!;
    const whole = normalizeCountry(seg);
    if (whole) return whole;
    const folded = foldText(seg);
    if (!NON_COUNTRY_REGIONS.some((r) => containsWord(folded, r))) {
      for (const [name, code] of TEXT_NAMES) if (containsWord(folded, name)) return code;
    }
    if (i === lines.length - 1) {
      for (const [city, code] of CITY_ENTRIES) if (containsWord(folded, city)) return code;
    }
  }
  return null;
}

/**
 * Empfängerland (ST-15): an der Rechnung/dem Deal eingefrorenes Land → Rechnungsadresse →
 * Land der Marke. Die Adresse schlägt das Markenland (ST-14).
 */
export function resolveRecipientCountry(input: {
  frozenCountry?: string | null;
  address?: string | readonly string[] | null;
  brandCountry?: string | null;
}): { country: CountryCode | null; source: "frozen" | "address" | "brand" | "none" } {
  const frozen = normalizeCountry(input.frozenCountry ?? null);
  if (frozen) return { country: frozen, source: "frozen" };
  const fromAddress = countryFromAddress(input.address ?? null);
  if (fromAddress) return { country: fromAddress, source: "address" };
  const brand = normalizeCountry(input.brandCountry ?? null);
  if (brand) return { country: brand, source: "brand" };
  return { country: null, source: "none" };
}
